import { useEffect, useRef } from 'react';
import { useTheme } from '@vitness/theme';
import { patternImage, PATTERN_TILE, PATTERN_DEFAULT, type PatternKind } from '@vitness/ui';

export interface ShellBackgroundProps {
  /** Render the soft accent glow ellipse behind the content. Defaults to true. */
  glow?: boolean;
  /** Light the dot-grid under the cursor via a radial mask. Defaults to true. */
  cursorLit?: boolean;
  /** Background pattern: tiled VITNESS logo mark, or the classic dot-grid. Defaults to 'logo'. */
  pattern?: PatternKind;
}

/** Shell ink colour; opacity differs for the base vs. cursor-lit layer. */
function shellInk(isLight: boolean, strong: boolean): string {
  const alpha = isLight ? (strong ? 0.1 : 0.04) : strong ? 0.15 : 0.06;
  return isLight ? `rgba(0,0,0,${alpha})` : `rgba(255,255,255,${alpha})`;
}

/**
 * The signature shell content background: a faint 32px dot-grid that lights up
 * under the cursor through a radial mask, plus a soft accent-glow ellipse behind
 * the content. No photography, noise, or heavy gradients — pure technical
 * surface. Render it as the first child of a `position: relative` scroll
 * container; it paints at `z-index: 0` so content sits above at `z-index: 1`.
 *
 * Mirrors the landing Hero / Auth treatment so every surface reads as one system.
 */
export function ShellBackground({ glow = true, cursorLit = true, pattern = PATTERN_DEFAULT }: ShellBackgroundProps) {
  const { accentRgb, mode } = useTheme();
  const isLight = mode === 'light';
  const rgb = `${accentRgb.r},${accentRgb.g},${accentRgb.b}`;

  const rootRef = useRef<HTMLDivElement>(null);
  const litRef = useRef<HTMLDivElement>(null);

  /**
   * Fork change: the cursor position is written straight to the lit layer as
   * CSS custom properties rather than held in React state.
   *
   * Upstream called `setMouse` from the mousemove handler, so every frame of
   * cursor movement re-rendered this component *and* asked React to reconcile
   * whatever tree it sits in. That's invisible on a marketing page and brutal
   * over a data grid: moving the mouse across the Expenses table meant ~60
   * React renders a second on top of the repaint, and the main thread never
   * caught up.
   *
   * Reading the rect once per frame inside the rAF callback also forced a
   * synchronous layout every frame; it's now cached and refreshed on resize.
   */
  useEffect(() => {
    if (!cursorLit) return;

    let frame = 0;
    let rect = rootRef.current?.getBoundingClientRect() ?? null;
    const measure = () => { rect = rootRef.current?.getBoundingClientRect() ?? null; };

    const onMove = (event: MouseEvent) => {
      if (frame) return;
      const { clientX, clientY } = event;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (!rect || !litRef.current) return;
        litRef.current.style.setProperty('--vn-lit-x', `${clientX - rect.left}px`);
        litRef.current.style.setProperty('--vn-lit-y', `${clientY - rect.top}px`);
      });
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('resize', measure, { passive: true });
    window.addEventListener('scroll', measure, { passive: true, capture: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, { capture: true });
      if (frame) cancelAnimationFrame(frame);
    };
  }, [cursorLit]);

  return (
    <div
      ref={rootRef}
      aria-hidden="true"
      style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden' }}
    >
      {/* Base grid */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: patternImage(shellInk(isLight, false), pattern),
          backgroundSize: `${PATTERN_TILE}px ${PATTERN_TILE}px`,
        }}
      />
      {/* Cursor-lit grid */}
      {cursorLit && (
        <div
          ref={litRef}
          style={{
            position: 'absolute',
            inset: 0,
            // Starts offscreen so the lit layer is hidden until the cursor
            // first moves; the effect above updates these two variables.
            ['--vn-lit-x' as string]: '-9999px',
            ['--vn-lit-y' as string]: '-9999px',
            backgroundImage: patternImage(shellInk(isLight, true), pattern),
            backgroundSize: `${PATTERN_TILE}px ${PATTERN_TILE}px`,
            WebkitMaskImage: 'radial-gradient(500px circle at var(--vn-lit-x) var(--vn-lit-y), black, transparent)',
            maskImage: 'radial-gradient(500px circle at var(--vn-lit-x) var(--vn-lit-y), black, transparent)',
          } as React.CSSProperties}
        />
      )}
      {/* Accent glow */}
      {glow && (
        <div
          style={{
            position: 'absolute',
            top: -220,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 900,
            height: 700,
            background: `radial-gradient(ellipse, rgba(${rgb},${isLight ? 0.04 : 0.06}) 0%, transparent 65%)`,
          }}
        />
      )}
    </div>
  );
}
